"use client";
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useClerk } from '@clerk/nextjs';
import clsx from 'clsx';
import {
  LayoutDashboard,
  UserCircle,
  CalendarClock,
  ChevronDown,
  Loader2,
  Star,
  CheckCircle2,
  UserRound,
  FileText,
  LogOut,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePreceptorStore } from '@/store/preceptor-store';
import type { PreceptorProfile } from '@/types/preceptor';

const links = [
  { label: 'Dashboard', href: '/preceptor/dashboard', icon: LayoutDashboard },
  { label: 'Profile', href: '/preceptor/profile', icon: UserCircle },
  { label: 'Booking Schedule', href: '/preceptor/booking-schedule', icon: CalendarClock },
];

function composeProfileName(profile: PreceptorProfile, index: number): string {
  const firstName = profile.first_name?.trim();
  const lastName = profile.last_name?.trim();

  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }

  const siteName = profile.site?.name?.trim();
  if (siteName) {
    return siteName;
  }

  return `Profile #${profile.id ?? index + 1}`;
}

export function PreceptorSideNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { signOut } = useClerk();
  const profiles = usePreceptorStore((state) => state.profiles);
  const activeProfileId = usePreceptorStore((state) => state.activeProfileId);
  const defaultProfileId = usePreceptorStore((state) => state.defaultProfileId);
  const loading = usePreceptorStore((state) => state.loading);
  const saving = usePreceptorStore((state) => state.saving);
  const loaded = usePreceptorStore((state) => state.loaded);
  const fetchProfile = usePreceptorStore((state) => state.fetchProfile);
  const setActiveProfile = usePreceptorStore((state) => state.setActive);

  const [profilesOpen, setProfilesOpen] = useState(() => pathname.startsWith('/preceptor/profiles'));
  const [activatingId, setActivatingId] = useState<number | null>(null);

  useEffect(() => {
    if (!loaded && !loading) {
      void fetchProfile();
    }
  }, [loaded, loading, fetchProfile]);

  useEffect(() => {
    if (pathname.startsWith('/preceptor/profiles')) {
      setProfilesOpen(true);
    }
  }, [pathname]);

  const profileItems = useMemo(() => {
    return profiles
      .filter((profile) => profile.status !== 'pending' && profile.status !== 'draft')
      .map((profile, idx) => {
      const name = composeProfileName(profile, idx);
      const isActive = profile.id ? profile.id === activeProfileId : false;
      const isDefault = profile.id ? profile.id === defaultProfileId : false;

      return {
        key: `profile-${profile.id ?? idx}`,
        label: name,
        isActive,
        isDefault,
        id: profile.id,
      };
      });
  }, [profiles, activeProfileId, defaultProfileId]);

  const handleProfileSelect = async (profileId?: number) => {
    if (!profileId || saving) {
      return;
    }
    setActivatingId(profileId);
    try {
      await setActiveProfile(profileId);
      router.push('/preceptor/profile');
    } catch (error) {
      console.error('Failed to activate profile', error);
    } finally {
      setActivatingId(null);
    }
  };

  const currentProfileLabel = useMemo(() => {
    if (profiles.length === 0) {
      return 'Profiles';
    }

    const activeProfile = profiles.find((profile) => profile.id === activeProfileId);
    if (activeProfile) {
      const index = profiles.findIndex((profile) => profile.id === activeProfile.id);
      return composeProfileName(activeProfile, index >= 0 ? index : 0);
    }

    const defaultProfile = profiles.find((profile) => profile.id === defaultProfileId);
    if (defaultProfile) {
      const index = profiles.findIndex((profile) => profile.id === defaultProfile.id);
      return composeProfileName(defaultProfile, index >= 0 ? index : 0);
    }

    return composeProfileName(profiles[0], 0);
  }, [profiles, activeProfileId, defaultProfileId]);

  return (
    <nav className="w-56 shrink-0 border-r border-[var(--brand-border)] bg-white/80 backdrop-blur flex flex-col fixed top-16 left-0 h-[calc(100vh-4rem)]">
      <div className="p-4 flex-1 overflow-y-auto">
        <button
          type="button"
          onClick={() => setProfilesOpen((prev) => !prev)}
          className={clsx(
            'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
            pathname.startsWith('/preceptor/profiles')
              ? 'bg-[var(--brand-primary)] text-white shadow-sm'
              : 'text-[var(--brand-muted)] hover:bg-[var(--brand-light)] hover:text-foreground'
          )}
          aria-expanded={profilesOpen}
          aria-controls="preceptor-profile-menu"
        >
          <span className="flex items-center gap-2">
            <UserCircle className="h-4 w-4" aria-hidden="true" />
            {currentProfileLabel}
          </span>
          <ChevronDown
            className={clsx('h-4 w-4 transition-transform', profilesOpen ? 'rotate-180' : 'rotate-0')}
            aria-hidden="true"
          />
        </button>
        {profilesOpen && (
          <ul id="preceptor-profile-menu" className="mt-1 max-h-64 space-y-1 overflow-y-auto pl-6 pr-1">
            {loading && profiles.length === 0 ? (
              <li className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--brand-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading profiles...
              </li>
            ) : (
              profileItems.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    disabled={saving || activatingId === item.id}
                    onClick={() => handleProfileSelect(item.id)}
                    className={clsx(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                      item.isActive
                        ? 'bg-[var(--brand-primary)] text-white shadow-sm'
                        : 'text-[var(--brand-muted)] hover:bg-[var(--brand-light)] hover:text-foreground',
                      (saving || activatingId === item.id) && 'opacity-70 cursor-not-allowed'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {item.isActive && <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}
                      {!item.isActive && item.isDefault && <Star className="h-3.5 w-3.5" aria-hidden="true" />}
                      {item.label}
                    </span>
                    {activatingId === item.id && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                  </button>
                </li>
              ))
            )}
            <li>
              <Link
                href="/preceptor/profiles"
                className={clsx(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  pathname === '/preceptor/profiles'
                    ? 'bg-[var(--brand-primary)] text-white shadow-sm'
                    : 'text-[var(--brand-muted)] hover:bg-[var(--brand-light)] hover:text-foreground'
                )}
              >
                <Star className="h-4 w-4" aria-hidden="true" />
                Manage profiles
              </Link>
            </li>
          </ul>
        )}
        <ul className="mt-4 space-y-1">
          {links.map((l) => {
            const active = pathname === l.href;
            const Icon = l.icon;
            return (
              <li key={l.href}>
                <Link
                  href={l.href}
                  className={clsx(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    active
                      ? 'bg-[var(--brand-primary)] text-white shadow-sm'
                      : 'text-[var(--brand-muted)] hover:bg-[var(--brand-light)] hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {l.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="border-t border-[var(--brand-border)] bg-white/90 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--brand-muted)] transition-colors hover:bg-[var(--brand-light)] hover:text-foreground"
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              Settings
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={10}
            className="w-48 border-[var(--brand-border)] bg-white/95 shadow-lg"
          >
            <DropdownMenuItem asChild>
              <Link href="/preceptor/user-profile" className="flex items-center gap-2">
                <UserCircle className="h-4 w-4" aria-hidden="true" />
                <span>User profile</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/terms" className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                <span>Terms</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOut({ redirectUrl: '/' })}
              className="flex items-center gap-2 text-red-600 focus:text-red-600"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
